import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Paper, Chip, CircularProgress, IconButton, Tooltip
} from '@mui/material';
import ReceiptIcon from '@mui/icons-material/Receipt';
import RefreshIcon from '@mui/icons-material/Refresh';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip, ResponsiveContainer } from 'recharts';
import api from '../api';
import { useSede } from '../contexto/Sede';
import Tile, { REJILLA_TILES, compacto } from './Tile';
import { useEsMovil } from '../estilos/responsivo';
import { BLOQUE, TARJETA_INTERACTIVA, TARJETITA, TITULO_BLOQUE } from '../estilos/tarjeta';

const estadoColor = {
  borrador: '#94a3b8',
  emitida: '#3b82f6',
  pagada: '#10b981',
  parcial: '#f59e0b',
  vencida: '#ef4444',
  anulada: '#cbd5e1',
};

export default function Dashboard() {
  const esMovil = useEsMovil();
  const { params: paramsSede, nombre: nombreSede, sedeId } = useSede();
  const [data, setData] = useState(null);
  const [contab, setContab] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const fetchData = async () => {
    try {
      const [resumen, contRes] = await Promise.all([
        api.get('/dashboard/resumen', { params: paramsSede }),
        api.get('/dashboard/contabilidad').catch(() => ({ data: null })),
      ]);
      setData(resumen.data);
      setContab(contRes.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchData(); }, [sedeId]);

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}><CircularProgress /></Box>;
  if (!data) return <Typography color="error">Error al cargar dashboard</Typography>;

  // Fondo tintado y cifra compacta, como las ventas del día de Entretur.
  // Antes eran tarjetas con ícono, degradado y sombra: el adorno ocupaba el
  // espacio que necesitaba el número.
  const kpis = [
    { label: 'Facturado del mes', v: data.facturado_mes, color: '#059669', bg: '#d1fae5' },
    { label: 'Cobrado del mes',   v: data.cobrado_mes,   color: '#1d4ed8', bg: '#dbeafe' },
    { label: 'Pendiente de cobro', v: data.pendiente,    color: '#a16207', bg: '#fef3c7' },
    { label: 'Vencido',           v: data.vencido,       color: '#b91c1c', bg: '#fee2e2' },
  ];

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between',
                 alignItems: 'center', mb: 2, gap: 1, flexWrap: 'wrap' }}>
        <Box>
          <Typography sx={{ color: '#64748b', fontSize: 13 }}>
            {data.total_facturas} facturas | {data.total_clientes} clientes | {data.borradores} borradores
          </Typography>
        </Box>
        <Tooltip title="Actualizar">
          <IconButton onClick={fetchData}><RefreshIcon /></IconButton>
        </Tooltip>
      </Box>

      {/* KPIs */}
      <Box sx={{ ...REJILLA_TILES, mb: 1 }}>
        {kpis.map((k) => (
          <Tile key={k.label} label={k.label} v={k.v} color={k.color} bg={k.bg} />
        ))}
      </Box>

      {/* Tendencia + Recientes */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '2fr 1fr' }, gap: 1 }}>
        {/* Grafico */}
        <Paper elevation={0} sx={BLOQUE}>
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 1,
                     flexWrap: 'wrap' }}>
            <Typography sx={TITULO_BLOQUE}>
              Ingresos mensuales
            </Typography>
            {/* La leyenda de recharts se comía un tercio del alto en el
                teléfono. Dos series se explican con dos puntos de color. */}
            <Box sx={{ display: 'flex', gap: 1.5, ml: 'auto' }}>
              {[['Facturado', '#1e3a5f'], ['Cobrado', '#10b981']].map(([t, c]) => (
                <Box key={t} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: 0.5, bgcolor: c }} />
                  <Typography sx={{ fontSize: 11, color: '#64748b' }}>{t}</Typography>
                </Box>
              ))}
            </Box>
          </Box>
          {/* El alto sigue al ancho: 280px fijos daban una barra estirada y
              flaca en el teléfono y achatada en el monitor. */}
          <ResponsiveContainer width="100%" height={esMovil ? 180 : 220}>
            <BarChart data={data.tendencia}
              margin={{ top: 4, right: 4, left: -8, bottom: 0 }}
              barGap={2}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
              <XAxis dataKey="mes" tickLine={false} axisLine={false}
                interval="preserveStartEnd" minTickGap={8}
                tick={{ fontSize: esMovil ? 10 : 12, fill: '#64748b' }} />
              {/* Sin compactar, «17861900» dejaba el eje más ancho que el
                  gráfico. */}
              <YAxis tickLine={false} axisLine={false} width={esMovil ? 44 : 56}
                tick={{ fontSize: esMovil ? 10 : 12, fill: '#64748b' }}
                tickFormatter={compacto} />
              <RTooltip
                formatter={(v, n) => [
                  '$' + new Intl.NumberFormat('es-CO',
                    { maximumFractionDigits: 0 }).format(Number(v || 0)), n]}
                contentStyle={{ fontSize: 12, borderRadius: 8,
                                border: '1px solid #e2e8f0' }} />
              <Bar dataKey="facturado" name="Facturado" fill="#1e3a5f"
                radius={[3, 3, 0, 0]} maxBarSize={28} />
              <Bar dataKey="cobrado" name="Cobrado" fill="#10b981"
                radius={[3, 3, 0, 0]} maxBarSize={28} />
            </BarChart>
          </ResponsiveContainer>
        </Paper>

        {/* Facturas recientes: lleva chips de estado y cada factura se
            abre, así que va con el fondo común y tarjetitas blancas. */}
        <Paper elevation={0} sx={TARJETA_INTERACTIVA}>
          <Typography sx={{ ...TITULO_BLOQUE, mb: 1 }}>
            Facturas recientes
          </Typography>
          {data.facturas_recientes.map((f, i) => (
            <Box key={i} onClick={() => navigate(`/facturas/${f.id}`)} sx={{
              ...TARJETITA, mb: 0.5, cursor: 'pointer', transition: 'background-color .15s',
              '&:hover': { bgcolor: '#f8fafc' }
            }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontWeight: 600, fontSize: 13, color: '#1e293b' }}>{f.numero_factura}</Typography>
                  <Typography noWrap sx={{ fontSize: 11, color: '#64748b' }}>{f.tercero}</Typography>
                </Box>
                <Box sx={{ textAlign: 'right', flexShrink: 0, pl: 1 }}>
                  <Typography sx={{ fontWeight: 700, fontSize: 13, color: '#1e293b',
                                    fontVariantNumeric: 'tabular-nums' }}>
                    {'$' + new Intl.NumberFormat('es-CO',
                      { maximumFractionDigits: 0 }).format(Number(f.total || 0))}
                  </Typography>
                  <Chip label={f.estado} size="small" sx={{
                    height: 18, fontSize: 10, fontWeight: 600,
                    bgcolor: `${estadoColor[f.estado]}20`, color: estadoColor[f.estado],
                  }} />
                </Box>
              </Box>
            </Box>
          ))}
          {data.facturas_recientes.length === 0 && (
            <Box sx={{ textAlign: 'center', py: 3 }}>
              <ReceiptIcon sx={{ fontSize: 40, color: '#cbd5e1' }} />
              <Typography sx={{ color: '#94a3b8', fontSize: 13 }}>No hay facturas aun</Typography>
            </Box>
          )}
        </Paper>
      </Box>

      {/* Contabilidad */}
      {contab && contab.seeded && (
        <Box sx={{ mt: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <AccountTreeIcon sx={{ color: '#7c3aed', fontSize: 17 }} />
            <Typography sx={TITULO_BLOQUE}>Contabilidad NIIF</Typography>
            <Chip label={contab.balance_cuadrado ? 'Cuadrado' : 'Descuadrado'} size="small" sx={{
              height: 20, fontSize: 10, fontWeight: 600,
              bgcolor: contab.balance_cuadrado ? '#d1fae5' : '#fee2e2',
              color: contab.balance_cuadrado ? '#059669' : '#dc2626',
            }} />
          </Box>
          <Box sx={REJILLA_TILES}>
            {[
              { label: 'Activos',    v: contab.total_activos, color: '#1d4ed8', bg: '#dbeafe' },
              { label: 'Pasivos',    v: contab.total_pasivos, color: '#be185d', bg: '#fce7f3' },
              { label: 'Patrimonio', v: contab.patrimonio,    color: '#7c3aed', bg: '#ede9fe' },
              { label: 'Resultado del mes', v: contab.resultado_mes,
                color: (contab.resultado_mes || 0) < 0 ? '#b91c1c' : '#059669',
                bg: (contab.resultado_mes || 0) < 0 ? '#fee2e2' : '#d1fae5' },
            ].map((k) => (
              <Tile key={k.label} label={k.label} v={k.v} color={k.color} bg={k.bg}
                onClick={() => navigate('/contabilidad/reportes')} />
            ))}
          </Box>
          <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
            <Typography sx={{ fontSize: 11, color: '#94a3b8' }}>
              {contab.total_cuentas} cuentas | {contab.total_asientos} asientos contabilizados
            </Typography>
          </Box>
        </Box>
      )}
    </Box>
  );
}
